import * as React from "react";
import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";
import {RoleType} from "../../objects/Role";
import DateRange, {RangeToTimes} from "../../components/DateRange";
import moment from "moment/moment";
import Loading from "../../components/loading";
import {
    Alert,
    FormControl,
    Input,
    InputAdornment,
    ListSubheader,
    Menu,
    MenuItem,
    Paper,
    Popover,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableSortLabel
} from "@mui/material";
import Box from "@mui/material/Box";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import TableFooterRow from "../../components/TableFooterRow";
import {useAuth} from "../../providers/auth";
import {PermissionType} from "../../objects/UserRead";
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import SearchIcon from "@mui/icons-material/Search";
import {InfoOutlined} from "@mui/icons-material";
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import EditIcon from "@mui/icons-material/Edit";
import ClickWallExempt from "./clickWallExempt";
import ClickWallTimecard from "./clickWallTimecard";
import {toast} from "react-toastify";
import downloadCsv from "../../lib/downloadCsv";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

const TOTALS = {revenue: 0, profit: 0, submissions: 0, payable: 0, leads: 0, apps: 0, enrolls: 0, starts: 0, revenue_per_lead: 0, revenue_per_submission: 0, payable_percent: 0, apps_percent: 0, enrolls_percent: 0, starts_percent: 0, calls : 0, talks: 0, session_seconds: 0, calls_per_hour: 0, leads_per_talk: 0, work_seconds: 0, seat_cost: 0, submissions_per_hour : 0,
    leads_group1: 0, leads_group2: 0, leads_group3: 0, leads_group4: 0, leads_group5: 0, apps_group1: 0, apps_group2: 0, apps_group3: 0, apps_group4: 0, apps_group5: 0, apps_percent_group1: 0, apps_percent_group2: 0, apps_percent_group3: 0, apps_percent_group4: 0, apps_percent_group5: 0, submissions_per_lead: 0, bonus: 0 };

const team_colors = ['inherit', '#faa9a7', '#2589f5'];
const TEAM_LEADS = [
    {id: 150, name: 'Alicia'},
    {id: 161, name: 'Ciarra'},
];
const TIMEZONE_OPTIONS = [
    {value: 'America/New_York', label: 'ny', menu: 'Eastern (NY)'},
    {value: 'America/Chicago', label: 'ch', menu: 'Central (CH)'},
    {value: 'America/Phoenix', label: 'phx', menu: 'Arizona (PHX)'},
    {value: 'America/Denver', label: 'den', menu: 'Mountain (DEN)'},
    {value: 'America/Los_Angeles', label: 'la', menu: 'Pacific (LA)'},
];

function timezoneLabel(tz: string | null | undefined) {
    const found = TIMEZONE_OPTIONS.find((t) => t.value === tz);
    if (found) return found.label;
    if (!tz) return '—';
    return tz.split('/').pop()?.replace(/_/g, ' ').toLowerCase() || '—';
}

const timezoneBadgeStyle: React.CSSProperties = {
    fontSize: '9px',
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    display: 'inline-block',
    verticalAlign: 'middle',
    marginLeft: '4px',
    padding: '1px 5px',
    borderRadius: '3px',
    backgroundColor: 'rgba(0,0,0,0.08)',
    color: 'rgba(0,0,0,0.65)',
    lineHeight: 1.4,
};

function computeLeaderboardTotals(rows: any[]) {
    const totals = {...TOTALS};
    rows.forEach((x: any) => {
        totals.revenue += x.revenue;
        totals.submissions += x.submissions;
        totals.payable += x.payable;
        totals.leads += x.leads;
        totals.apps += x.apps;
        totals.enrolls += x.enrolls;
        totals.starts += x.starts;
        totals.calls += x.calls;
        totals.talks += x.talks;
        totals.session_seconds += x.session_seconds;
        totals.work_seconds += x.work_seconds;
        totals.seat_cost += x.seat_cost;
        totals.leads_group1 += x.leads_group1;
        totals.leads_group2 += x.leads_group2;
        totals.leads_group3 += x.leads_group3;
        totals.leads_group4 += x.leads_group4;
        totals.leads_group5 += x.leads_group5;
        totals.apps_group1 += x.apps_group1;
        totals.apps_group2 += x.apps_group2;
        totals.apps_group3 += x.apps_group3;
        totals.apps_group4 += x.apps_group4;
        totals.apps_group5 += x.apps_group5;
        // Est. Bonus total excludes negatives (BEM-1228)
        if ((Number(x.bonus) || 0) > 0) {
            totals.bonus += x.bonus;
        }
    });
    totals.revenue_per_lead = totals.leads ? totals.revenue / totals.leads : 0;
    totals.revenue_per_submission = totals.submissions ? totals.revenue / totals.submissions : 0;
    totals.payable_percent = totals.submissions ? totals.payable / totals.submissions * 100 : 0;
    totals.apps_percent = totals.leads ? totals.apps / totals.leads * 100 : 0;
    totals.apps_percent_group1 = totals.leads_group1 ? totals.apps_group1 / totals.leads_group1 * 100 : 0;
    totals.apps_percent_group2 = totals.leads_group2 ? totals.apps_group2 / totals.leads_group2 * 100 : 0;
    totals.apps_percent_group3 = totals.leads_group3 ? totals.apps_group3 / totals.leads_group3 * 100 : 0;
    totals.apps_percent_group4 = totals.leads_group4 ? totals.apps_group4 / totals.leads_group4 * 100 : 0;
    totals.apps_percent_group5 = totals.leads_group5 ? totals.apps_group5 / totals.leads_group5 * 100 : 0;
    totals.enrolls_percent = totals.leads ? totals.enrolls / totals.leads * 100 : 0;
    totals.starts_percent = totals.leads ? totals.starts / totals.leads * 100 : 0;
    totals.calls_per_hour = totals.session_seconds ? totals.calls / totals.session_seconds * 3600 : 0;
    totals.leads_per_talk = totals.talks ? (totals.leads / totals.talks * 100) : 0;
    totals.submissions_per_hour = totals.session_seconds ? totals.submissions / totals.session_seconds * 3600 : 0;
    totals.submissions_per_lead = totals.leads ? totals.submissions / totals.leads : 0;
    return totals;
}

export default function ClickWallLeaderboard() {
    const [leaderboard, setLeaderboard] = React.useState([] as any[]);
    const [breakdown, setBreakdown] = React.useState([] as any[]);
    const [breakdownNames, setBreakdownNames] = React.useState([] as any[]);
    const [breakdownTalks, setBreakdownTalks] = React.useState([] as any[]);
    const [loading, setLoading] = React.useState(true);
    const [dateRange, setDateRange] = React.useState(RangeToTimes("Today") as any);
    const [agentSearch, setAgentSearch] = React.useState("");
    const [sortConfig, setSortConfig] = React.useState<{ key: string | null, direction: 'asc' | 'desc' }>({key: null, direction: 'asc'});
    const [anchorEls, setAnchorEls] = React.useState<any>({});
    const [exempt, setExempt] = React.useState<any>({agent: '', seconds: 0, date: ''});
    const [timecard, setTimecard] = React.useState<any>({agent: '', seconds: 0, date: ''});
    const [cohorts, setCohorts] = React.useState({} as any);
    let auth = useAuth();

    React.useEffect(() => {
        document.title = "Call Center Leaderboard | DN";
    }, []);

    const loadLeaderboard = async (value?: any) => {
        const range = (value?.[0] && value?.[1]) ? value : dateRange;
        if (!range?.[0] || !range?.[1]) return;
        const q = gql`
            query q($startDate: String!, $endDate: String!) {
                clickWallLeaderboard(startDate: $startDate, endDate: $endDate) {
                    agent
                    rep_id
                    team_lead_id
                    timezone
                    submissions
                    payable
                    revenue
                    leads
                    apps
                    enrolls
                    starts
                    apps_percent
                    enrolls_percent
                    starts_percent
                    revenue_per_lead
                    revenue_per_submission
                    payable_percent
                    calls
                    talks
                    session_seconds
                    work_seconds
                    seat_cost
                    seat_cost_exempt
                    seat_cost_exempt_seconds
                    leads_group1
                    leads_group2
                    leads_group3
                    leads_group4
                    leads_group5
                    apps_group1
                    apps_group2
                    apps_group3
                    apps_group4
                    apps_group5
                    apps_percent_group1
                    apps_percent_group2
                    apps_percent_group3
                    apps_percent_group4
                    apps_percent_group5
                    apps_percent_group1_cohort
                    apps_percent_group2_cohort
                    apps_percent_group3_cohort
                    apps_percent_group4_cohort
                    apps_percent_group5_cohort
                }
                clickWallLeaderboardBreakdown(startDate: $startDate, endDate: $endDate) {
                    agent
                    school
                    status
                    total
                }
                clickWallLeaderboardLeadNameBreakdown(startDate: $startDate, endDate: $endDate) {
                    agent
                    name
                    total
                }
                clickWallLeaderboardTalkBreakdown(startDate: $startDate, endDate: $endDate) {
                    agent
                    name
                    total
                }
            }
        `;

        const variables = {
            startDate: moment(range[0]).format("YYYY-MM-DD HH:mm:ss"),
            endDate: moment(range[1]).format("YYYY-MM-DD HH:mm:ss"),
        }

        setLoading(true);
        const response = await query(q, variables);

        let ch = {1: 0, 2: 0, 3: 0, 4: 0, 5: 0};
        if (response && response.clickWallLeaderboard) {
            setLeaderboard(response.clickWallLeaderboard);
            setBreakdown(response.clickWallLeaderboardBreakdown);
            setBreakdownNames(response.clickWallLeaderboardLeadNameBreakdown);
            setBreakdownTalks(response.clickWallLeaderboardTalkBreakdown);

            response.clickWallLeaderboard.forEach((x: any, index: number) => {
                x.defaultIndex = index;
                if (auth.user?.role.type === RoleType.CALL_CENTER_REP
                && (x.agent === "Lauren Sands" || (x.rep_id > 0 && x.rep_id === x.team_lead_id))
                ) {
                    x.leads = 0;
                    x.submissions = 0;
                    x.talks = 0;
                    x.payable = 0;
                    x.calls = 0;
                    x.submissions_per_lead = 0;
                    x.calls_per_submission = 0;
                    x.apps = 0;
                    x.enrolls = 0
                    x.starts = 0;
                    x.apps_percent = 0;
                    x.enrolls_percent = 0;
                    x.starts_percent = 0;
                    x.payable_percent = 0;

                }
                if (!ch[1] && x.apps_percent_group1_cohort) {
                    ch[1] = x.apps_percent_group1_cohort;
                }
                if (!ch[2] && x.apps_percent_group2_cohort) {
                    ch[2] = x.apps_percent_group2_cohort;
                }

                if (!ch[3] && x.apps_percent_group3_cohort) {
                    ch[3] = x.apps_percent_group3_cohort;
                }
                if (!ch[4] && x.apps_percent_group4_cohort) {
                    ch[4] = x.apps_percent_group4_cohort;
                }

                if (!ch[5] && x.apps_percent_group5_cohort) {
                    ch[5] = x.apps_percent_group5_cohort;
                }

                if (x.rep_id === 0 || x.team_lead_id === 0) {
                    x.colorIndex = 0;
                } else {
                    x.colorIndex = x.team_lead_id === 150 ? 1 : 2;
                }

                x.bonus = (x.revenue - x.seat_cost + x.seat_cost_exempt) * 0.1;
                if (x.agent.includes("Alyssa")) {
                    x.bonus = 0;
                }
                //if start date < 2025-04-01 set bonus to 0
                if (moment(range[0]).isBefore("2025-04-01")) {
                    x.bonus = 0;
                }

                x.submissions_per_lead = x.leads ? x.submissions / x.leads : 0;
            });
        }
        setCohorts(ch);

        setLoading(false);
    }

    React.useEffect(() => {
        loadLeaderboard(RangeToTimes("Today")).then(() => {
        });
    }, []);

    const isAdmin = !!auth.user?.permissions.includes(PermissionType.Admin);
    const isManager = !!auth.user?.permissions.includes(PermissionType.Admin) || auth.user?.role.type === RoleType.CALL_CENTER_MANAGER;
    const canEditTimezone = (repId: number) =>
        !!repId && (
            isManager
            || (!!auth.user?.permissions.includes(PermissionType.CallCenter) && auth.user?.id === repId)
            || repId === 999999
        );

    const handleSetTeamLead = async (repId: number, teamLeadId: number) => {
        setAnchorEls({...anchorEls, [`team-${repId}`]: null});
        const q = gql`
            mutation m($repId: Int!, $teamLeadId: Int!) {
                clickWallSetTeamLead(repId: $repId, teamLeadId: $teamLeadId)
            }
        `;
        const response = await mutate(q, {repId, teamLeadId});
        if (response && response.clickWallSetTeamLead) {
            toast.success('Team lead updated');
            setLeaderboard((prev) => prev.map((row: any) => {
                if (row.rep_id !== repId) {
                    return row;
                }
                return {
                    ...row,
                    team_lead_id: teamLeadId,
                    colorIndex: teamLeadId === 150 ? 1 : teamLeadId === 161 ? 2 : 0,
                };
            }));
        }
    };

    const handleSetTimezone = async (repId: number, timezone: string) => {
        setAnchorEls({...anchorEls, [`tz-${repId}`]: null});
        const row = leaderboard.find((r: any) => r.rep_id === repId);
        // Dev stub agent — update UI only, no DB write
        if (row?.agent === 'Test Agent' || repId === 999999) {
            setLeaderboard((prev) => prev.map((r: any) =>
                r.rep_id === repId ? {...r, timezone} : r));
            toast.success('Timezone updated (local test)');
            return;
        }
        const q = gql`
            mutation m($repId: Int!, $timezone: String!) {
                clickWallSetTimezone(repId: $repId, timezone: $timezone)
            }
        `;
        const response = await mutate(q, {repId, timezone});
        if (response && response.clickWallSetTimezone) {
            toast.success('Timezone updated');
            setLeaderboard((prev) => prev.map((r: any) =>
                r.rep_id === repId ? {...r, timezone} : r));
        }
    };

    // compare that dateRange[1] and dateRange[2] are the same date:
    const isSame = dateRange[0] && dateRange[0].getDate() === dateRange[1].getDate() && dateRange[0].getMonth() === dateRange[1].getMonth();
    let dateString = '';
    if (isSame) {
        dateString = moment(dateRange[0]).format('YYYY-MM-DD');
    }
    const todayString = moment().format('YYYY-MM-DD');
    const canEditTimecard = !!dateString && dateString !== todayString;

    const filteredLeaderboard = React.useMemo(() => {
        const q = agentSearch.trim().toLowerCase();
        if (!q) {
            return leaderboard;
        }
        return leaderboard.filter((row: any) =>
            String(row.agent ?? "").toLowerCase().includes(q));
    }, [leaderboard, agentSearch]);

    const displayTotals = React.useMemo(
        () => computeLeaderboardTotals(filteredLeaderboard),
        [filteredLeaderboard]);

    const getSortValue = React.useCallback((row: any, key: string) => {
        switch (key) {
            case 'rank':
                return row.defaultIndex ?? 0;
            case 'agent':
                return String(row.agent ?? '');
            case 'submissions_per_hour':
                return row.session_seconds ? (row.submissions / row.session_seconds * 3600) : 0;
            case 'calls_per_hour':
                return row.session_seconds ? row.calls / row.session_seconds * 3600 : 0;
            case 'leads_per_talk':
                return row.talks ? row.leads / row.talks * 100 : 0;
            default:
                return row[key] ?? 0;
        }
    }, []);

    const sortTypeByKey: Record<string, 'string' | 'number'> = {
        rank: 'number',
        agent: 'string',
        revenue: 'number',
        seat_cost: 'number',
        seat_cost_exempt: 'number',
        bonus: 'number',
        submissions: 'number',
        submissions_per_lead: 'number',
        submissions_per_hour: 'number',
        calls: 'number',
        calls_per_hour: 'number',
        talks: 'number',
        leads_per_talk: 'number',
        session_seconds: 'number',
        work_seconds: 'number',
        payable: 'number',
        leads: 'number',
        apps: 'number',
        enrolls: 'number',
        starts: 'number',
        apps_percent_group1: 'number',
        apps_percent_group2: 'number',
        apps_percent_group3: 'number',
        apps_percent_group4: 'number',
        apps_percent_group5: 'number',
    };

    const onSort = React.useCallback((key: string) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {key, direction: prev.direction === 'asc' ? 'desc' : 'asc'};
            }
            return {key, direction: sortTypeByKey[key] === 'string' ? 'asc' : 'desc'};
        });
    }, [sortTypeByKey]);

    const sortedLeaderboard = React.useMemo(() => {
        if (!sortConfig.key) {
            return filteredLeaderboard;
        }
        const next = [...filteredLeaderboard];
        next.sort((a: any, b: any) => {
            const av = getSortValue(a, sortConfig.key as string);
            const bv = getSortValue(b, sortConfig.key as string);
            if (sortTypeByKey[sortConfig.key as string] === 'string') {
                const stringCompare = String(av).localeCompare(String(bv), undefined, {sensitivity: 'base'});
                return sortConfig.direction === 'asc' ? stringCompare : -stringCompare;
            }
            const an = Number(av) || 0;
            const bn = Number(bv) || 0;
            const numberCompare = an - bn;
            return sortConfig.direction === 'asc' ? numberCompare : -numberCompare;
        });
        return next;
    }, [filteredLeaderboard, getSortValue, sortConfig, sortTypeByKey]);

    const sortLabel = (label: React.ReactNode, key: string) => (
        <TableSortLabel
            active={sortConfig.key === key}
            direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
            onClick={() => onSort(key)}
            sx={{userSelect: 'text'}}
        >
            {label}
        </TableSortLabel>
    );

    const downloadLeaderboard = async () => {
        if (!sortedLeaderboard.length) {
            toast.error("Nothing to export");
            return;
        }

        const money = (n: number) =>
            `$${(Number(n) || 0).toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}`;
        const int = (n: number) =>
            (Number(n) || 0).toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0});
        const one = (n: number) =>
            (Number(n) || 0).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1});
        const two = (n: number) =>
            (Number(n) || 0).toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2});
        const pct = (n: number) => `${one(n)}%`;
        const appsLeads = (apps: number, leads: number, percent: number) =>
            `${int(apps)} / ${int(leads)} (${pct(percent)})`;

        const content: any[][] = [[
            "#",
            "Agent",
            "Revenue",
            "Seat Cost",
            "Seat Exempt",
            "Est. Bonus",
            "Submissions",
            "Revenue/Submission",
            "Sub/Lead",
            "Sub/Hr",
            "Calls",
            "Calls/Hr",
            "Talks",
            "Leads/Talk",
            "Session Duration",
            "Time Card",
            "Payable",
            "Payable %",
            "Leads",
            "Revenue/Lead",
            "Apps",
            "Apps %",
            "Enrolls",
            "Enrolls %",
            "Starts",
            "Starts %",
            "Apps UAGC",
            "Apps SNHU",
            "Apps GCU",
            "Apps Post",
            "Apps South",
        ]];
        sortedLeaderboard.forEach((row: any, index: number) => {
            const subHr = row.session_seconds ? (row.submissions / row.session_seconds * 3600) : 0;
            const callsHr = row.session_seconds ? (row.calls / row.session_seconds * 3600) : 0;
            const leadsPerTalk = row.talks ? row.leads / row.talks * 100 : 0;
            content.push([
                index + 1,
                row.agent,
                money(row.revenue),
                money(row.seat_cost),
                money(row.seat_cost_exempt),
                money(row.bonus),
                int(row.submissions),
                money(row.revenue_per_submission),
                one(row.submissions_per_lead),
                `${two(subHr)}/hr`,
                int(row.calls),
                `${int(callsHr)}/hr`,
                int(row.talks),
                pct(leadsPerTalk),
                secondsToTime(row.session_seconds || 0),
                secondsToTime(row.work_seconds || 0),
                int(row.payable),
                pct(row.payable_percent),
                int(row.leads),
                money(row.revenue_per_lead),
                int(row.apps),
                pct(row.apps_percent),
                int(row.enrolls),
                pct(row.enrolls_percent),
                int(row.starts),
                pct(row.starts_percent),
                appsLeads(row.apps_group1, row.leads_group1, row.apps_percent_group1),
                appsLeads(row.apps_group2, row.leads_group2, row.apps_percent_group2),
                appsLeads(row.apps_group3, row.leads_group3, row.apps_percent_group3),
                appsLeads(row.apps_group4, row.leads_group4, row.apps_percent_group4),
                appsLeads(row.apps_group5, row.leads_group5, row.apps_percent_group5),
            ]);
        });
        const start = dateRange?.[0] ? moment(dateRange[0]).format("YYYY-MM-DD") : "start";
        const end = dateRange?.[1] ? moment(dateRange[1]).format("YYYY-MM-DD") : "end";
        await downloadCsv(`call_center_leaderboard_${start}_${end}.csv`, content);
    };

    return (
        <>
            <h2>Call Center Leaderboard</h2>
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "center", width: "100%", gap: 1}}>
                <DateRange showTime={true} dateRange={dateRange} setDateRange={setDateRange}
                           style={{marginRight: '10px'}}/>
                <Button variant="contained" onClick={() => loadLeaderboard()} disabled={loading || !dateRange?.[0]}>
                    Go
                </Button>
                {isAdmin && sortedLeaderboard.length > 0 && (
                    <IconButton aria-label="download" onClick={downloadLeaderboard} title="Download CSV">
                        <FileDownloadIcon/>
                    </IconButton>
                )}
                <FormControl variant="outlined" sx={{m: 1, maxWidth: 190, ml: "auto"}} size="small">
                    <Input
                        id="agent-search"
                        type="text"
                        spellCheck="false"
                        placeholder="Search"
                        value={agentSearch || ""}
                        onChange={(e: any) => setAgentSearch(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon/></InputAdornment>}
                    />
                </FormControl>
            </Box>
            {loading ? <Loading/> :
                <>
                    <p></p>
                    {!leaderboard || leaderboard.length === 0 ? <Alert severity="warning">No submissions found</Alert> :
                     filteredLeaderboard.length === 0 ? <Alert severity="info">No agents match your search</Alert> :
                        <>
                            <TableContainer component={Paper}
                                            sx={{maxWidth: 'calc(100vw - 10px)', maxHeight: 'calc(100vh - 200px)'}}>
                                <Table size="small">
                                    <TableHead sx={{
                                        userSelect: 'text',
                                        '& th': {userSelect: 'text'},
                                    }}>
                                        <TableHeaderRow>
                                            <TableCell>{sortLabel('#', 'rank')}</TableCell>
                                            <TableCell>{sortLabel('Agent', 'agent')}</TableCell>
                                            {isAdmin && <TableCell  align="right">{sortLabel('Revenue', 'revenue')}</TableCell>}
                                            {isManager && <>
                                                <TableCell  align="right">{sortLabel('Seat Cost', 'seat_cost')}</TableCell>
                                                <TableCell  align="right">{sortLabel('Seat Exempt', 'seat_cost_exempt')}</TableCell>
                                            </>}
                                            <TableCell  align="right">{sortLabel('Est. Bonus', 'bonus')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Submissions', 'submissions')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Sub/Lead', 'submissions_per_lead')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Sub/Hr', 'submissions_per_hour')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Calls', 'calls')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Talks', 'talks')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Leads/Talk', 'leads_per_talk')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Session Duration', 'session_seconds')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Time Card', 'work_seconds')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Payable', 'payable')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Leads', 'leads')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Apps', 'apps')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Enrolls', 'enrolls')}</TableCell>
                                            <TableCell  align="right">{sortLabel('Starts', 'starts')}</TableCell>
                                            {isAdmin && <>
                                                <TableCell  align="right">
                                                    {sortLabel(<>
                                                        Apps UAGC ({cohorts['1'].toFixed(1)}%)
                                                        <InfoOutlined sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, ['Gr1-SUBMISSION']: e.currentTarget})}/>
                                                    </>, 'apps_percent_group1')}
                                                    <Popover
                                                        id={'Gr1-SUBMISSION'}
                                                        open={!!anchorEls['Gr1-SUBMISSION']}
                                                        anchorEl={anchorEls['Gr1-SUBMISSION']}
                                                        onClose={() => setAnchorEls({...anchorEls, ['Gr1-SUBMISSION']: null})}
                                                    >
                                                    <Box sx={{fontSize: '13px', padding: '5px'}}>DN, UAGC, SF</Box>
                                                    </Popover>
                                                </TableCell>
                                                <TableCell  align="right">
                                                    {sortLabel(<>
                                                        Apps SNHU ({cohorts['2'].toFixed(1)}%)
                                                        <InfoOutlined sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, ['Gr2-SUBMISSION']: e.currentTarget})}/>
                                                    </>, 'apps_percent_group2')}
                                                    <Popover
                                                        id={'Gr2-SUBMISSION'}
                                                        open={!!anchorEls['Gr2-SUBMISSION']}
                                                        anchorEl={anchorEls['Gr2-SUBMISSION']}
                                                        onClose={() => setAnchorEls({...anchorEls, ['Gr2-SUBMISSION']: null})}
                                                    >
                                                        <Box sx={{fontSize: '13px', padding: '5px'}}>DN, SNHU, LO</Box>
                                                    </Popover>
                                                </TableCell>
                                                <TableCell  align="right">
                                                    {sortLabel(<>
                                                        Apps GCU ({cohorts['3'].toFixed(1)}%)
                                                        <InfoOutlined sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, ['Gr3-SUBMISSION']: e.currentTarget})}/>
                                                    </>, 'apps_percent_group3')}
                                                    <Popover
                                                        id={'Gr3-SUBMISSION'}
                                                        open={!!anchorEls['Gr3-SUBMISSION']}
                                                        anchorEl={anchorEls['Gr3-SUBMISSION']}
                                                        onClose={() => setAnchorEls({...anchorEls, ['Gr3-SUBMISSION']: null})}
                                                    >
                                                        <Box sx={{fontSize: '13px', padding: '5px'}}>DN, GCU, SF</Box>
                                                    </Popover>
                                                </TableCell>
                                                <TableCell  align="right">
                                                    {sortLabel(<>
                                                        Apps Post ({cohorts['4'].toFixed(1)}%)
                                                        <InfoOutlined sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, ['Gr4-SUBMISSION']: e.currentTarget})}/>
                                                    </>, 'apps_percent_group4')}
                                                    <Popover
                                                        id={'Gr4-SUBMISSION'}
                                                        open={!!anchorEls['Gr4-SUBMISSION']}
                                                        anchorEl={anchorEls['Gr4-SUBMISSION']}
                                                        onClose={() => setAnchorEls({...anchorEls, ['Gr4-SUBMISSION']: null})}
                                                    >
                                                        <Box sx={{fontSize: '13px', padding: '5px'}}>DN, Post, SF</Box>
                                                    </Popover>
                                                </TableCell>
                                                <TableCell  align="right">
                                                    {sortLabel(<>
                                                        Apps South ({cohorts['5'].toFixed(1)}%)
                                                        <InfoOutlined sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, ['Gr5-SUBMISSION']: e.currentTarget})}/>
                                                    </>, 'apps_percent_group5')}
                                                    <Popover
                                                        id={'Gr5-SUBMISSION'}
                                                        open={!!anchorEls['Gr5-SUBMISSION']}
                                                        anchorEl={anchorEls['Gr5-SUBMISSION']}
                                                        onClose={() => setAnchorEls({...anchorEls, ['Gr5-SUBMISSION']: null})}
                                                    >
                                                        <Box sx={{fontSize: '13px', padding: '5px'}}>DN, South, SF</Box>
                                                    </Popover>
                                                </TableCell>
                                            </>}
                                        </TableHeaderRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedLeaderboard.map((row: any, index: number) => (
                                            <React.Fragment key={row.agent}>
                                                <StripedTableRow>
                                                    <TableCell>
                                                        <b>{index + 1}.</b>
                                                    </TableCell>
                                                    <TableCell sx={{backgroundColor: team_colors[row.colorIndex]}}>
                                                        {isManager && !(row.rep_id > 0 && row.rep_id === row.team_lead_id) ? (
                                                            <>
                                                                <a
                                                                    style={{textDecoration: 'underline', cursor: 'pointer'}}
                                                                    onClick={(e) => setAnchorEls({...anchorEls, [`team-${row.rep_id}`]: e.currentTarget})}
                                                                >
                                                                    {row.agent}
                                                                </a>
                                                                <Menu
                                                                    anchorEl={anchorEls[`team-${row.rep_id}`]}
                                                                    open={!!anchorEls[`team-${row.rep_id}`]}
                                                                    onClose={() => setAnchorEls({...anchorEls, [`team-${row.rep_id}`]: null})}
                                                                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                                                                    transformOrigin={{vertical: 'top', horizontal: 'left'}}
                                                                    PaperProps={{
                                                                        sx: {
                                                                            minWidth: 160,
                                                                            borderRadius: '8px',
                                                                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                                                            overflow: 'hidden',
                                                                        }
                                                                    }}
                                                                    MenuListProps={{sx: {py: 0}}}
                                                                >
                                                                    <ListSubheader
                                                                        disableSticky
                                                                        sx={{
                                                                            lineHeight: '32px',
                                                                            fontSize: '11px',
                                                                            fontWeight: 700,
                                                                            letterSpacing: '0.06em',
                                                                            textTransform: 'uppercase',
                                                                            color: 'common.white',
                                                                            backgroundColor: 'primary.main',
                                                                            py: 0,
                                                                        }}
                                                                    >
                                                                        Team Lead
                                                                    </ListSubheader>
                                                                    {TEAM_LEADS.map((lead) => {
                                                                        const isSelected = row.team_lead_id === lead.id;
                                                                        const leadColor = lead.id === 150 ? team_colors[1] : team_colors[2];
                                                                        return (
                                                                        <MenuItem
                                                                            key={lead.id}
                                                                            selected={isSelected}
                                                                            onClick={() => {handleSetTeamLead(row.rep_id, lead.id)}}
                                                                            sx={{
                                                                                py: 1,
                                                                                px: 1.5,
                                                                                fontSize: '14px',
                                                                                fontWeight: isSelected ? 700 : 400,
                                                                                borderLeft: `4px solid ${leadColor}`,
                                                                                '&.Mui-selected': {
                                                                                    backgroundColor: `${leadColor}55`,
                                                                                },
                                                                                '&.Mui-selected:hover': {
                                                                                    backgroundColor: `${leadColor}77`,
                                                                                },
                                                                                '&:hover': {
                                                                                    backgroundColor: `${leadColor}33`,
                                                                                },
                                                                            }}
                                                                        >
                                                                            {isSelected
                                                                                ? <DoneOutlineIcon sx={{marginRight: 1, fontSize: '18px', color: leadColor === team_colors[2] ? leadColor : '#c62828'}} />
                                                                                : <Box sx={{width: '26px', flexShrink: 0}} />}
                                                                            {lead.name}
                                                                        </MenuItem>
                                                                        );
                                                                    })}
                                                                </Menu>
                                                            </>
                                                        ) : (
                                                            <>{row.agent} {row.rep_id > 0 && row.rep_id === row.team_lead_id && <MilitaryTechIcon sx={{verticalAlign: 'middle', fontSize: '22px'}}/>}</>
                                                        )}
                                                        {row.rep_id > 0 && (
                                                            <>
                                                                {' '}
                                                                {canEditTimezone(row.rep_id) ? (
                                                                    <a
                                                                        style={{
                                                                            ...timezoneBadgeStyle,
                                                                            textDecoration: 'none',
                                                                            cursor: 'pointer',
                                                                        }}
                                                                        onClick={(e) => setAnchorEls({...anchorEls, [`tz-${row.rep_id}`]: e.currentTarget})}
                                                                    >
                                                                        {timezoneLabel(row.timezone)}
                                                                    </a>
                                                                ) : (
                                                                    <span style={timezoneBadgeStyle}>
                                                                        {timezoneLabel(row.timezone)}
                                                                    </span>
                                                                )}
                                                                {canEditTimezone(row.rep_id) && (
                                                                <Menu
                                                                    anchorEl={anchorEls[`tz-${row.rep_id}`]}
                                                                    open={!!anchorEls[`tz-${row.rep_id}`]}
                                                                    onClose={() => setAnchorEls({...anchorEls, [`tz-${row.rep_id}`]: null})}
                                                                    anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                                                                    transformOrigin={{vertical: 'top', horizontal: 'left'}}
                                                                    PaperProps={{
                                                                        sx: {
                                                                            minWidth: 180,
                                                                            borderRadius: '8px',
                                                                            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                                                            overflow: 'hidden',
                                                                        }
                                                                    }}
                                                                    MenuListProps={{sx: {py: 0}}}
                                                                >
                                                                    <ListSubheader
                                                                        disableSticky
                                                                        sx={{
                                                                            lineHeight: '32px',
                                                                            fontSize: '11px',
                                                                            fontWeight: 700,
                                                                            letterSpacing: '0.06em',
                                                                            textTransform: 'uppercase',
                                                                            color: 'common.white',
                                                                            backgroundColor: 'primary.main',
                                                                            py: 0,
                                                                        }}
                                                                    >
                                                                        Timezone
                                                                    </ListSubheader>
                                                                    {TIMEZONE_OPTIONS.map((tz) => {
                                                                        const isSelected = row.timezone === tz.value;
                                                                        return (
                                                                            <MenuItem
                                                                                key={tz.value}
                                                                                selected={isSelected}
                                                                                onClick={() => handleSetTimezone(row.rep_id, tz.value)}
                                                                                sx={{
                                                                                    py: 1,
                                                                                    px: 1.5,
                                                                                    fontSize: '14px',
                                                                                    fontWeight: isSelected ? 700 : 400,
                                                                                }}
                                                                            >
                                                                                {isSelected
                                                                                    ? <DoneOutlineIcon sx={{marginRight: 1, fontSize: '18px'}} />
                                                                                    : <Box sx={{width: '26px', flexShrink: 0}} />}
                                                                                {tz.menu}
                                                                            </MenuItem>
                                                                        );
                                                                    })}
                                                                </Menu>
                                                                )}
                                                            </>
                                                        )}
                                                    </TableCell>
                                                    {isAdmin && (
                                                    <TableCell align="right">
                                                        ${row.revenue.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}
                                                    </TableCell>
                                                    )}
                                                    {isManager && <>
                                                    <TableCell align="right">
                                                        ${row.seat_cost.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}
                                                    </TableCell>
                                                        <TableCell align="right">
                                                            {(!!row.seat_cost && !!dateString) && <a style={{textDecoration: 'underline', cursor: 'pointer'}} onClick={() => {setExempt({agent: row.agent, seconds: row.seat_cost_exempt_seconds, date: dateString})}}>${row.seat_cost_exempt.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</a>}
                                                            {(!row.seat_cost || !dateString) && <>${row.seat_cost_exempt.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</>}
                                                        </TableCell>
                                                        </>}
                                                    <TableCell
                                                        align="right"
                                                        sx={
                                                            (isManager || (row.rep_id === auth.user?.id) || (row.team_lead_id === auth.user?.id)) &&
                                                            row.bonus < 0
                                                                ? {color: "error.main", fontWeight: 700}
                                                                : undefined
                                                        }
                                                    >
                                                        {isManager || (row.rep_id === auth.user?.id) || (row.team_lead_id === auth.user?.id) ? `$${row.bonus.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}` : "-"}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.submissions > 0 && <ZoomInIcon sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, [row.agent+'-SUBMISSION']: e.currentTarget})}/>}
                                                        <Popover
                                                            id={row.agent+'-SUBMISSION'}
                                                            open={!!anchorEls[row.agent+'-SUBMISSION']}
                                                            anchorEl={anchorEls[row.agent+'-SUBMISSION']}
                                                            onClose={() => setAnchorEls({...anchorEls, [row.agent+'-SUBMISSION']: null})}
                                                        >
                                                          <Breakdown agent={row.agent} breakdown={breakdown} status="SUBMISSION"/>
                                                        </Popover>
                                                        {row.submissions.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                        {isAdmin && <>&nbsp;(${row.revenue_per_submission.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})})</>}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.submissions_per_lead.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{background: subHrColor(row.session_seconds ? (row.submissions / row.session_seconds * 3600) : 0)}}>
                                                        {(row.session_seconds ? (row.submissions / row.session_seconds * 3600) : 0).toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}/hr
                                                    </TableCell>
                                                    <TableCell align="right" sx={{background: callsPerHourColor(row.session_seconds, (row.session_seconds ? row.calls / row.session_seconds * 3600 : 0))}}>
                                                        {row.calls.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                        &nbsp;({(row.session_seconds ? row.calls / row.session_seconds * 3600 : 0).toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}/hr)
                                                    </TableCell>
                                                    <TableCell align="right">
                                                    {row.talks > 0 && <ZoomInIcon sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, [row.agent+'-TALK']: e.currentTarget})}/>}
                                                     <Popover
                                                            id={row.agent+'-TALK'}
                                                            open={!!anchorEls[row.agent+'-TALK']}
                                                            anchorEl={anchorEls[row.agent+'-TALK']}
                                                            onClose={() => setAnchorEls({...anchorEls, [row.agent+'-TALK']: null})}
                                                        >
                                                            <BreakdownTalks agent={row.agent} breakdown={breakdownTalks}/>
                                                        </Popover>                                                        
                                                        {row.talks.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {(row.talks ? row.leads / row.talks * 100 : 0).toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {secondsToTime(row.session_seconds)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{background: occopancyPercentColor(row.work_seconds, row.session_seconds)}}>
                                                        {(isManager || (row.rep_id === auth.user?.id) || (row.team_lead_id === auth.user?.id)) ? (
                                                            (canEditTimecard && isManager) ? (
                                                                <a style={{textDecoration: 'underline', cursor: 'pointer'}}
                                                                   onClick={() => {setTimecard({agent: row.agent, seconds: row.work_seconds, date: dateString})}}>
                                                                    {secondsToTime(row.work_seconds)}
                                                                </a>
                                                            ) : secondsToTime(row.work_seconds)
                                                        ) : "-"}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.payable.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                        &nbsp;({row.payable_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.leads > 0 && <ZoomInIcon sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, [row.agent+'-LEAD']: e.currentTarget})}/>}
                                                        <Popover
                                                            id={row.agent+'-LEAD'}
                                                            open={!!anchorEls[row.agent+'-LEAD']}
                                                            anchorEl={anchorEls[row.agent+'-LEAD']}
                                                            onClose={() => setAnchorEls({...anchorEls, [row.agent+'-LEAD']: null})}
                                                        >
                                                            <BreakdownLead agent={row.agent} breakdown={breakdownNames}/>
                                                        </Popover>
                                                        {row.leads.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                        {isAdmin && <>&nbsp;(${row.revenue_per_lead.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})})</>}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.apps > 0 && <ZoomInIcon sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, [row.agent+'-APP']: e.currentTarget})}/>}
                                                        <Popover
                                                            id={row.agent+'-APP'}
                                                            open={!!anchorEls[row.agent+'-APP']}
                                                            anchorEl={anchorEls[row.agent+'-APP']}
                                                            onClose={() => setAnchorEls({...anchorEls, [row.agent+'-APP']: null})}
                                                        >
                                                            <Breakdown agent={row.agent} breakdown={breakdown} status="APP"/>
                                                        </Popover>
                                                        {row.apps.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                        &nbsp;({row.apps_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.enrolls > 0 && <ZoomInIcon sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, [row.agent+'-ENROLL']: e.currentTarget})}/>}
                                                        <Popover
                                                            id={row.agent+'-ENROLL'}
                                                            open={!!anchorEls[row.agent+'-ENROLL']}
                                                            anchorEl={anchorEls[row.agent+'-ENROLL']}
                                                            onClose={() => setAnchorEls({...anchorEls, [row.agent+'-ENROLL']: null})}
                                                        >
                                                            <Breakdown agent={row.agent} breakdown={breakdown} status="ENROLL"/>
                                                        </Popover>
                                                        {row.enrolls.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                        &nbsp;({row.enrolls_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {row.starts > 0 && <ZoomInIcon sx={{verticalAlign: 'middle', fontSize: '22px', cursor: 'pointer'}} onClick={(e) => setAnchorEls({...anchorEls, [row.agent+'-START']: e.currentTarget})}/>}
                                                        <Popover
                                                            id={row.agent+'-START'}
                                                            open={!!anchorEls[row.agent+'-START']}
                                                            anchorEl={anchorEls[row.agent+'-START']}
                                                            onClose={() => setAnchorEls({...anchorEls, [row.agent+'-START']: null})}
                                                        >
                                                            <Breakdown agent={row.agent} breakdown={breakdown} status="START"/>
                                                        </Popover>
                                                        {row.starts.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                        &nbsp;({row.starts_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                    </TableCell>
                                                    {isAdmin && <>
                                                        <TableCell align="right">
                                                            {row.apps_group1.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {row.leads_group1.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                            &nbsp;({row.apps_percent_group1.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.apps_group2.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {row.leads_group2.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                            &nbsp;({row.apps_percent_group2.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.apps_group3.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {row.leads_group3.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                            &nbsp;({row.apps_percent_group3.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.apps_group4.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {row.leads_group4.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                            &nbsp;({row.apps_percent_group4.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {row.apps_group5.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {row.leads_group5.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                            &nbsp;({row.apps_percent_group5.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                        </TableCell>
                                                    </>}
                                                </StripedTableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableFooterRow>
                                            <TableCell colSpan={2}>Totals</TableCell>
                                            {isAdmin && (
                                            <TableCell align="right">${displayTotals.revenue.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                            )}
                                            {isManager && <>
                                            <TableCell align="right">${displayTotals.seat_cost.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}</TableCell>
                                             <TableCell align="right"></TableCell>
                                            </>}
                                            <TableCell align="right">
                                                {isManager
                                                    ? `$${displayTotals.bonus.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}`
                                                    : ""}
                                            </TableCell>
                                            <TableCell align="right">{displayTotals.submissions.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                {isAdmin && <> &nbsp;(${displayTotals.revenue_per_submission.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})})</>}
                                            </TableCell>
                                            <TableCell align="right">{displayTotals.submissions_per_lead.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}</TableCell>
                                            <TableCell align="right">{displayTotals.submissions_per_hour.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})}/hr</TableCell>
                                            <TableCell align="right">
                                                {displayTotals.calls.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                &nbsp;({displayTotals.calls_per_hour.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}/hr)
                                            </TableCell>
                                            <TableCell align="right">
                                                {displayTotals.talks.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                            </TableCell>
                                            <TableCell align="right">
                                                {displayTotals.leads_per_talk.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%
                                            </TableCell>
                                            <TableCell align="right">
                                                {secondsToTime(displayTotals.session_seconds)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {isManager ? secondsToTime(displayTotals.work_seconds) : "-"}
                                            </TableCell>
                                            <TableCell align="right">{displayTotals.payable.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                            &nbsp;({displayTotals.payable_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)</TableCell>
                                            <TableCell align="right">{displayTotals.leads.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                {isAdmin && <>&nbsp;(${displayTotals.revenue_per_lead.toLocaleString(undefined, {maximumFractionDigits: 2, minimumFractionDigits: 2})})</>}</TableCell>
                                            <TableCell align="right">{displayTotals.apps.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                            &nbsp;({displayTotals.apps_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)</TableCell>
                                            <TableCell align="right">{displayTotals.enrolls.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                            &nbsp;({displayTotals.enrolls_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)</TableCell>
                                            <TableCell align="right">{displayTotals.starts.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                            &nbsp;({displayTotals.starts_percent.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)</TableCell>
                                            {isAdmin && <>
                                                <TableCell align="right">
                                                    {displayTotals.apps_group1.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {displayTotals.leads_group1.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                    &nbsp;({displayTotals.apps_percent_group1.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                </TableCell>
                                                <TableCell align="right">
                                                    {displayTotals.apps_group2.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {displayTotals.leads_group2.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                    &nbsp;({displayTotals.apps_percent_group2.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                </TableCell>
                                                <TableCell align="right">
                                                    {displayTotals.apps_group3.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {displayTotals.leads_group3.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                    &nbsp;({displayTotals.apps_percent_group3.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                </TableCell>
                                                <TableCell align="right">
                                                    {displayTotals.apps_group4.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {displayTotals.leads_group4.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                    &nbsp;({displayTotals.apps_percent_group4.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                </TableCell>
                                                <TableCell align="right">
                                                    {displayTotals.apps_group5.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})} / {displayTotals.leads_group5.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                                                    &nbsp;({displayTotals.apps_percent_group5.toLocaleString(undefined, {maximumFractionDigits: 1, minimumFractionDigits: 1})}%)
                                                </TableCell>
                                            </>}
                                        </TableFooterRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </>
                    }
            </>}
            <ClickWallExempt exempt={exempt} setExempt={setExempt} reload={() => loadLeaderboard()}/>
            <ClickWallTimecard timecard={timecard} setTimecard={setTimecard} reload={() => loadLeaderboard()}/>
        </>
    );
}

function BreakdownLead({ agent, breakdown}: { agent: string, breakdown: any[] }) {
    return (
        <Table size="small">
            <TableHead>
                <TableHeaderRow>
                    <TableCell>
                        Name
                    </TableCell>
                    <TableCell>
                        Total
                    </TableCell>
                </TableHeaderRow>
            </TableHead>
            <TableBody>
                {breakdown.filter(x=>x.agent === agent).map((br: any, i: number) => (
                    <StripedTableRow key={i}>
                        <TableCell>
                            {br.name}
                        </TableCell>
                        <TableCell align={"right"}>
                            {br.total.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                        </TableCell>
                    </StripedTableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function BreakdownTalks({ agent, breakdown}: { agent: string, breakdown: any[] }) {
    return (
        <Table size="small">
            <TableHead>
                <TableHeaderRow>
                    <TableCell>
                        Name
                    </TableCell>
                </TableHeaderRow>
            </TableHead>
            <TableBody>
                {breakdown.filter(x=>x.agent === agent).map((br: any, i: number) => (
                    <StripedTableRow key={i}>
                        <TableCell>
                            {br.name}
                        </TableCell>
                    </StripedTableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function Breakdown({ agent, breakdown, status}: { agent: string, breakdown: any[], status: string }) {
    return (
        <Table size="small">
            <TableHead>
                <TableHeaderRow>
                    <TableCell>
                        School
                    </TableCell>
                    <TableCell>
                        Total
                    </TableCell>
                </TableHeaderRow>
            </TableHead>
            <TableBody>
                {breakdown.filter(x=>x.agent === agent && x.status === status).map((br: any, i: number) => (
                    <StripedTableRow key={i}>
                        <TableCell>
                            {br.school}
                        </TableCell>
                        <TableCell align={"right"}>
                            {br.total.toLocaleString(undefined, {maximumFractionDigits: 0, minimumFractionDigits: 0})}
                        </TableCell>
                    </StripedTableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function subHrColor(subHr: number) {
    if (subHr >= 2) {
        return 'lightgreen';
    } else if (subHr >= 1.25 && subHr < 2) {
        return 'inherit';
    } else if (subHr >= 1.00 && subHr < 1.25) {
        return 'yellow';
    } else if (subHr > 0) {
        return 'pink';
    }

    return 'inherit';
}
function occopancyPercentColor(workSeconds: number, callSeconds: number) {
    if (workSeconds < callSeconds) {
        return 'pink'
    }

    if (workSeconds < 60 * 60) {
        return 'inherit';
    }

    const percent = callSeconds / workSeconds;

    if (percent > 0.9) {
        return 'lightgreen';
    } else if (percent >= 0.7 && percent < 0.74) {
        return 'yellow';
    } else if (percent < 0.7) {
        return 'pink';
    }

    return 'inherit';
}

function callsPerHourColor(seconds: number, cph: number) {
    if (seconds < 60 * 60) {
        return 'inherit';
    }
    if (cph >= 50) {
        return 'lightgreen';
    } else if (cph >= 20 && cph < 25) {
        return 'yellow';
    } else if (cph >= 1 && cph < 20) {
        return 'pink';
    }

    return 'inherit';
}
function secondsToTime(seconds: number) {
    let hours = Math.floor(seconds / 3600);
    let minutes = Math.floor((seconds % 3600) / 60);
    let seconds2 = seconds % 60;

    // get time with 2 digits in each

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds2.toString().padStart(2, '0')}`;


}
